import { ChangeDetectionStrategy, Component, inject, OnDestroy } from '@angular/core';

import { ReplaySubject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { DatePipe } from '@angular/common';
import { optionalTranslate } from '@language';
import { isNumber } from 'lodash-es';
import isString from 'lodash-es/isString';
import { BaseFieldDefinition, FieldType } from '../../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../../core/profiles/entities/extended-profile-field.entity';
import { PartialRecord } from '../../../../../core/records/entities/partial-record.class';
import { FormSelectOption } from '../../../../../form-controls/entities/form-select-option.entity';
import { toFormSelectOption } from '../../../../../form-controls/helpers/form-select-option-helper';
import { isNotUndefined } from '../../../../../helpers/typed-reactive-filters.helper';
import { FieldReadHelper } from '../../../../helpers/fields/field-read.helper';
import { ShSearchRoutingService } from '../../../../services/search-routing.service';
import { ShComplexReadonlyFieldComponent } from '../complex-readonly-field/complex-readonly-field.component';

@Component({
    standalone: false,
    selector: 'sh-list-readonly-field',
    templateUrl: './list-readonly-field.component.html',
    styleUrls: ['../../base-generic-readonly-field/base-generic-readonly-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShListReadonlyFieldComponent extends ShComplexReadonlyFieldComponent implements OnDestroy {
    public listValues$: ReplaySubject<FormSelectOption[]> = new ReplaySubject<FormSelectOption[]>(1);
    public FieldType: typeof FieldType = FieldType;

    public childDefinition!: BaseFieldDefinition;

    private readonly datePipe: DatePipe = inject(DatePipe);

    constructor(
        protected fieldHelper: FieldReadHelper,
        protected searchRoutingService: ShSearchRoutingService
    ) {
        super(fieldHelper, searchRoutingService);
    }

    protected initChildDefinitionStreams(): void {
        const childDefinition$ = this.profileField$.pipe(
            map(profileField => profileField.FieldDefinition.Children),
            isNotUndefined(),
            filter(val => Array.isArray(val)),
            map(children => children[0]),
            take(1),
            takeUntil(this.destroy$)
        );

        childDefinition$.subscribe(childDefinition => {
            this.childDefinition = childDefinition;
        });
    }

    init(profileField: ExtendedBaseProfileField, record: PartialRecord): void {
        const values: string[] = this.fieldHelper
            .getListFieldValue(profileField.FieldDefinition, record)
            .flatMap(children => children.value)
            .map(value => (isNumber(value) ? `${value}` : value))
            .filter(isString);

        const childDefinition: BaseFieldDefinition | undefined = profileField.FieldDefinition.Children?.filter(
            this.isNotNullOrUndefined
        )[0];

        if (this.isNotNullOrUndefined(childDefinition)) {
            this.listValues$.next(
                values.map(value =>
                    toFormSelectOption(
                        value,
                        optionalTranslate(
                            this.translateService,
                            childDefinition.FlatKey,
                            this.convertValue(value, childDefinition)
                        )
                    )
                )
            );
        } else {
            this.listValues$.next(values.map(value => toFormSelectOption(value)));
        }
    }

    private convertValue(value: string, childDefinition: BaseFieldDefinition): string {
        if (childDefinition.Type === FieldType.DateField) {
            return this.datePipe.transform(value, 'dd MMM yyyy') || value;
        }
        return value;
    }

    private isNotNullOrUndefined<T>(input: null | undefined | T): input is T {
        return input != null;
    }

    ngOnDestroy() {
        this.listValues$.complete();

        super.ngOnDestroy();
    }
}
